import React from "react";
import {Link} from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {Edit, Add, Delete} from "@material-ui/icons";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import {DragDropContext, Droppable, Draggable} from "react-beautiful-dnd";
import {toast} from "react-toastify";
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from "react-table";
import apiHelper from "../../../util/ApiHelper";
import {CustomCheckbox} from "../../../util";
import {useLoading} from "../../../util/LoadingProvider";
import {
    PaginationHandler
} from "../../../util/useManualPagination";
import PaginationBarForHandler from "../../common/PaginationBarForHandler";

const useStyles = makeStyles((theme) => ({
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
}));

const IndeterminateCheckbox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
                               ref={resolvedRef} {...rest} />;
    }
);

const FaqCategoryListingPage = (props) => {
    const {match, disabled} = props;
    const classes = useStyles();
    const [keyword, setKeyword] = React.useState("");
    const [data, setData] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(1);
    const [allRows, setAllRows] = React.useState([]); // Store all rows for sequence update
    const [orderingChanged, setOrderingChanged] = React.useState(false);

    const {setLoading} = useLoading();

    // for pagination
    const [paginationHandler] = React.useState(new PaginationHandler());
    const [elementCounts, setElementCounts] = React.useState({
        all: 0,
    });

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );

    const columns = React.useMemo(
        () => [
            {
                Header: "ID",
                accessor: "id",
            },
            {
                Header: "Name (EN)",
                accessor: "nameEn",
            },
            {
                Header: "Name (TC)",
                accessor: "nameTc",
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({row}) => {
                    const status = row.original.status;
                    return (
                        <span style={{
                            color: status ? "#4caf50" : "#999999"
                        }}>
                            {status ? "Active" : "Inactive"}
                        </span>
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
            initialState: {pageIndex: 0, pageSize: 10},
            manualPagination: true,
            pageCount: totalPages,
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: "selection",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({getToggleAllRowsSelectedProps}) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "action",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: () => <div>Action</div>,
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div
                            style={{display: "inline-flex", gap: "8px"}}>
                            <IconButton
                                size="small"
                                component={CustomLink}
                                to={`${match.url}/${row.original.id}/edit`}
                                title="Edit"
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteCategory(row.original.id, row.original.nameEn || "Category")}
                                title="Delete"
                                style={{ color: '#f44336' }}
                            >
                                <Delete fontSize="small"/>
                            </IconButton>
                        </div>
                    ),
                },
                {
                    id: "dragHandle",
                    Header: () => <></>,
                },
            ]);
        }
    );

    const getFaqCategory = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.cineclub.getMembershipFaqCategory();
            console.log('FAQ Category RESULT:', result)  
            
            var output = result.data.map(function(x){
                x.id = x.id.toString();
                return x;
            });
            
            // Sort by sequence if available
            output.sort((a, b) => {
                const seqA = a.sequence ?? 0;
                const seqB = b.sequence ?? 0;
                return seqA - seqB;
            });
            
            paginationHandler.setPageIndex(0).setRows(output);
            const all = output.length;
            const totalPages = Math.ceil(output.length / paginationHandler.pageSize);

            setElementCounts({
                all
            });
            setAllRows(output); // Store all rows
            setData(paginationHandler.setPageIndex(0).getRows());
            setTotalPages(totalPages);
            setOrderingChanged(false);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [paginationHandler, setLoading]);

    React.useEffect(() => {
        if (paginationHandler) {
            getFaqCategory(); // only get FAQ Category if paginationHandler ready
        }
    }, [getFaqCategory, paginationHandler]);

    const triggerSearch = React.useCallback(() => {
        console.log("KEYWORD: "+keyword);
        paginationHandler
            .setRows(allRows)
            .setQueryString(["id", "nameEn", "nameTc"], keyword)
            .filterRowsByAll();
        setData(paginationHandler.setPageIndex(0).getRows());
        setOrderingChanged(false); // Reset ordering changed state
        gotoPage(0);
    }, [gotoPage, keyword, paginationHandler, allRows]);

    const handleDeleteCategory = React.useCallback(async (categoryId, categoryName) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete the FAQ Category "${categoryName}"? This action cannot be undone.`
        );
        
        if (!confirmed) {
            return;
        }

        try {
            setLoading(true);
            const response = await apiHelper.cineclub.deleteMembershipFaqCategory(categoryId);
            
            if (response.data && response.data.code === 0) {
                // Success - refresh the FAQ Category list
                await getFaqCategory();
                alert("FAQ Category deleted successfully!");
            } else {
                console.error("Failed to delete FAQ Category:", response);
                alert("Failed to delete FAQ Category. Please try again.");
            }
        } catch (error) {
            console.error("Error deleting FAQ Category:", error);
            alert("An error occurred while deleting the FAQ Category. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [getFaqCategory, setLoading]);

    const onDragEnd = React.useCallback((result) => {
        if (!result.destination) {
            return;
        }

        if (result.source.index === result.destination.index) {
            return;
        }

        // Get the dragged row and destination row from current page
        const currentPageData = paginationHandler.getRows();
        const draggedItem = currentPageData[result.source.index];
        const destItem = currentPageData[result.destination.index];
        
        // Find indices in allRows by ID
        const sourceGlobalIndex = allRows.findIndex(row => row.id === draggedItem.id);
        const destGlobalIndex = allRows.findIndex(row => row.id === destItem.id);
        
        if (sourceGlobalIndex === -1 || destGlobalIndex === -1) {
            return; // Item not found in allRows
        }
        
        // Reorder the allRows array globally
        const allRowsCopy = [...allRows];
        const [removed] = allRowsCopy.splice(sourceGlobalIndex, 1);
        allRowsCopy.splice(destGlobalIndex, 0, removed);
        
        // Update pagination handler with reordered data
        paginationHandler.setRows(allRowsCopy);
        
        // Reapply filter if there's an active search
        if (keyword) {
            paginationHandler
                .setRows(allRowsCopy)
                .setQueryString(["id", "nameEn", "nameTc"], keyword)
                .filterRowsByAll();
        }
        
        // Update current page data (this will respect any active filters)
        const updatedPageData = paginationHandler.getRows();
        setData(updatedPageData);
        setAllRows(allRowsCopy);
        setOrderingChanged(true);
    }, [paginationHandler, allRows, keyword]);

    const saveOrdering = React.useCallback(async () => {
        try {
            setLoading(true);
            // Prepare sequence data: { id: sequence }
            const seqData = {};
            allRows.forEach((row, index) => {
                seqData[row.id] = index + 1;
            });
            
            const result = await apiHelper.cineclub.bulkUpdateMembershipFaqCategorySequence(seqData);
            
            if (result.data && result.data.code === 0) {
                toast.dark("Your changes have been saved.");
                setOrderingChanged(false);
                await getFaqCategory();
            } else {
                console.error("Failed to save ordering:", result);
                toast.error("Failed to save ordering. Please try again.");
            }
        } catch (error) {
            console.error("Error saving ordering:", error);
            toast.error("An error occurred while saving the ordering. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [allRows, getFaqCategory, setLoading]);

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                {"Cineclub > FAQ Category List"}
            </Typography>
            <form>
                <Grid
                    container
                    justify="space-between"
                    style={{paddingBottom: "10px"}}
                >
                    <Grid container item xs={6} md={6}>
                        <Box display="flex" alignItems="center">
                            <Button
                                variant="contained"
                                color="primary"
                                type="button"
                                onClick={saveOrdering}
                                disabled={!orderingChanged || disabled}
                                style={{ marginRight: "10px" }}
                            >
                                Save Ordering
                            </Button>
                        </Box>
                    </Grid>
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Box display="flex">
                                <TextField
                                    id="outlined-error"
                                    label="Search"
                                    variant="outlined"
                                    margin="dense"
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </Box>
                            <Box display="flex">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    type="button"
                                    margin="dense"
                                    onClick={triggerSearch}
                                >
                                    Search
                                </Button>
                            </Box>
                            <Box display="flex" style={{ marginRight: "10px" }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="button"
                                    component={Link}
                                    to={`${match.url}/create`}
                                    startIcon={<Add />}
                                >
                                    Create FAQ Category
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container xs={12}>
                    <TableContainer
                        component={Paper}
                        style={{
                            boxShadow: "none",
                            border: "1px solid #cccccc",
                        }}
                    >
                        <fieldset disabled={disabled}
                                  className="fieldset">
                            <Table
                                aria-label="simple table"
                                component="ul"
                                style={{margin: "0"}}
                                {...getTableProps()}
                            >
                                <colgroup>
                                    <col
                                        style={{width: "1%"}}/>
                                    <col
                                        style={{width: "10%"}}/>
                                    <col
                                        style={{width: "25%"}}/>
                                    <col
                                        style={{width: "25%"}}/>
                                    <col
                                        style={{width: "15%"}}/>
                                    <col
                                        style={{width: "14%"}}/>
                                    <col
                                        style={{width: "1%"}} />
                                </colgroup>
                                <TableHead component="div">
                                    {headerGroups.map((headerGroup) => (
                                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map((column) => (
                                                <TableCell
                                                    {...column.getHeaderProps()}
                                                    className={`${column.className ?? ""}`}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                        lineHeight: "1.1em",
                                                        height: "48px",
                                                    }}
                                                >
                                                    {column.render("Header")}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHead>
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="droppable" type="FAQ_CATEGORY">
                                        {(provided) => (
                                            <TableBody
                                                component="div"
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                            >
                                                {rows.map((row, index) => {
                                                    prepareRow(row);
                                                    return (
                                                        <Draggable
                                                            key={row.id}
                                                            draggableId={row.id}
                                                            index={index}
                                                            isDragDisabled={disabled}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <TableRow
                                                                    {...row.getRowProps()}
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    style={{
                                                                        ...provided.draggableProps.style,
                                                                        backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'transparent',
                                                                    }}
                                                                >
                                                                    {row.cells.map((cell) => {
                                                                        if (cell.column.id === "dragHandle") {
                                                                            return (
                                                                                <TableCell
                                                                                    key={cell.column.id}
                                                                                    style={{
                                                                                        paddingTop: 6,
                                                                                        paddingBottom: 6,
                                                                                        lineHeight: "1.1em",
                                                                                        height: "48px",
                                                                                        color: "#CCCCCC",
                                                                                    }}
                                                                                    {...(!disabled && provided.dragHandleProps)}
                                                                                >
                                                                                    <DragHandleIcon />
                                                                                </TableCell>
                                                                            );
                                                                        }
                                                                        return (
                                                                            <TableCell
                                                                                {...cell.getCellProps()}
                                                                                className={`${cell.column.className ?? ""}`}
                                                                                style={{
                                                                                    paddingTop: 6,
                                                                                    paddingBottom: 6,
                                                                                    lineHeight: "1.1em",
                                                                                    height: "48px",
                                                                                }}
                                                                            >
                                                                                {cell.render("Cell")}
                                                                            </TableCell>
                                                                        );
                                                                    })}
                                                                </TableRow>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                            </TableBody>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </Table>
                        </fieldset>
                    </TableContainer>
                </Grid>
                <Grid
                    container
                    justify="flex-end"
                    style={{
                        paddingRight: "25px",
                        marginTop: "15px"
                    }}
                >
                    <div>
                        <div></div>
                        <PaginationBarForHandler
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            setData={setData}
                            setTotalPages={setTotalPages}
                            paginationHandler={paginationHandler}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                            canPreviousPage={canPreviousPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canNextPage={canNextPage}
                            elementCounts={elementCounts}
                            status={null}
                        />
                    </div>
                </Grid>
            </form>
        </div>
    );
};

export default FaqCategoryListingPage;
